import { getMessages } from "@/i18n/runtime";
import type { Disposable } from "@/shared/contracts/lifecycle";

export type PurchaseSignInChoice = "sign-in" | "continue-as-guest" | "cancel";

export type PurchaseSignInPolicy = "recommend" | "require";

export interface OpenPurchaseSignInModalOptions {
  readonly parent?: HTMLElement;
  readonly policy: PurchaseSignInPolicy;
}

export interface PurchaseSignInModalHandle extends Disposable {
  readonly result: Promise<PurchaseSignInChoice>;
}

export function openPurchaseSignInModal(
  options: OpenPurchaseSignInModalOptions,
): PurchaseSignInModalHandle {
  const parent = options.parent ?? document.body;
  const messages = getMessages().payment.signInRecommendation;

  const backdrop = document.createElement("div");
  backdrop.className = "hover-idle-purchase-signin-modal-backdrop";

  const dialog = document.createElement("div");
  dialog.className = "hover-idle-purchase-signin-modal";
  dialog.setAttribute("role", "dialog");
  dialog.setAttribute("aria-modal", "true");
  dialog.setAttribute("aria-labelledby", "hover-idle-purchase-signin-title");
  dialog.tabIndex = -1;

  const title = document.createElement("h2");
  title.id = "hover-idle-purchase-signin-title";
  title.className = "hover-idle-purchase-signin-modal__title";
  title.textContent = messages.title;

  const body = document.createElement("div");
  body.className = "hover-idle-purchase-signin-modal__body";
  for (const line of messages.body) {
    const p = document.createElement("p");
    p.className = "hover-idle-purchase-signin-modal__line";
    p.textContent = line;
    body.append(p);
  }

  const actions = document.createElement("div");
  actions.className = "hover-idle-purchase-signin-modal__actions";

  const signInButton = document.createElement("button");
  signInButton.type = "button";
  signInButton.className =
    "hover-idle-purchase-signin-modal__action hover-idle-purchase-signin-modal__action--primary";
  signInButton.textContent = messages.actions.signIn;

  const guestButton = document.createElement("button");
  guestButton.type = "button";
  guestButton.className =
    "hover-idle-purchase-signin-modal__action hover-idle-purchase-signin-modal__action--guest";
  guestButton.textContent = messages.actions.continueAsGuest;
  if (options.policy === "require") {
    guestButton.disabled = true;
    guestButton.hidden = true;
  }

  const cancelButton = document.createElement("button");
  cancelButton.type = "button";
  cancelButton.className =
    "hover-idle-purchase-signin-modal__action hover-idle-purchase-signin-modal__action--cancel";
  cancelButton.textContent = messages.actions.cancel;

  actions.append(signInButton, guestButton, cancelButton);
  dialog.append(title, body, actions);
  backdrop.append(dialog);
  parent.append(backdrop);

  let isClosed = false;
  let resolveChoice: (choice: PurchaseSignInChoice) => void = () => {};
  const result = new Promise<PurchaseSignInChoice>((resolve) => {
    resolveChoice = resolve;
  });

  const close = (choice: PurchaseSignInChoice): void => {
    if (isClosed) return;
    isClosed = true;
    resolveChoice(choice);
  };

  const onSignIn = (): void => close("sign-in");
  const onGuest = (): void => {
    if (options.policy === "require") return;
    close("continue-as-guest");
  };
  const onCancel = (): void => close("cancel");
  const onBackdrop = (event: MouseEvent): void => {
    if (event.target === backdrop) close("cancel");
  };
  const onKeyDown = (event: KeyboardEvent): void => {
    if (event.key === "Escape") {
      event.stopPropagation();
      close("cancel");
    }
  };
  const onDialogClick = (event: MouseEvent): void => {
    event.stopPropagation();
  };

  signInButton.addEventListener("click", onSignIn);
  guestButton.addEventListener("click", onGuest);
  cancelButton.addEventListener("click", onCancel);
  backdrop.addEventListener("click", onBackdrop);
  dialog.addEventListener("click", onDialogClick);
  document.addEventListener("keydown", onKeyDown);

  queueMicrotask(() => {
    if (!isClosed) signInButton.focus({ preventScroll: true });
  });

  return {
    result,
    dispose(): void {
      close("cancel");
      signInButton.removeEventListener("click", onSignIn);
      guestButton.removeEventListener("click", onGuest);
      cancelButton.removeEventListener("click", onCancel);
      backdrop.removeEventListener("click", onBackdrop);
      dialog.removeEventListener("click", onDialogClick);
      document.removeEventListener("keydown", onKeyDown);
      backdrop.remove();
    },
  };
}
